import { emailRegex, invalidEmailErrorMessage, isValidPhone } from "@/utils/helpers/validationHelpers";

/**
 * React-admin compatible validators for InPlace editors.
 *
 * These follow react-admin's Validator signature:
 *   (value: any) => string | undefined
 *
 * Return `undefined` when valid, a string error message when invalid.
 * Null/undefined/empty values pass — use react-admin's `required()` for required fields.
 */

const ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/;
const US_PHONE_10_DIGIT = /^\d{10}$/;

/** Validates US zip code format: 5-digit or 5+4 (e.g. "12345" or "12345-6789") */
export const validateZipCode = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;
  return ZIP_CODE_REGEX.test(value) ? undefined : "Must be a valid zip code (e.g. 12345 or 12345-6789)";
};

/** Validates email format, reusing the regex from validationHelpers */
export const validateInPlaceEmail = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;
  return emailRegex.test(value) ? undefined : invalidEmailErrorMessage;
};

/** Validates that a numeric value is not negative */
export const validateNonNegative = (value: number | null | undefined): string | undefined => {
  if (value === null || value === undefined) return undefined;
  return value < 0 ? "Must be zero or greater" : undefined;
};

/**
 * Validates that a percentage value is within 0–1 range (stored as decimal).
 * InPlacePercentageEditor stores values as decimals (0.15 = 15%), so this
 * checks the storage value. Error message references 0–100% for user clarity.
 */
export const validatePercentageRange = (value: number | null | undefined): string | undefined => {
  if (value === null || value === undefined) return undefined;
  if (value < 0 || value > 1) return "Must be between 0% and 100%";
  return undefined;
};

/** Validates phone format, reusing the regex from validationHelpers */
export const validatePhone = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;
  const error = isValidPhone(value);
  return error || undefined;
};

/**
 * Normalizes a phone string to digits only, then formats as (XXX) XXX-XXXX for 10-digit US numbers.
 * Non-10-digit numbers are returned as digits only (no formatting applied).
 */
export const formatPhoneNumber = (value: string | null | undefined): string => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "");
  if (US_PHONE_10_DIGIT.test(digits)) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return digits;
};
